import React, { useEffect } from "react";
import CardsList from "./CardsList";
import {
  CardsService,
  CardTakeOnService,
  CardTakeOffService,
  CardSetHiddenService,
  CardSetRatioService,
  CardSetPacketService
} from "../../../utils/api";
import useGlobals from "../../../hooks/useGlobals";
import useNotifications from "../../../hooks/useNotifications";
import useEventEmitter from "../../../hooks/useEventEmitter";
import useThis from "../../../hooks/useThis";
import * as perm from "../../../utils/perm";
import usePrev from "../../../hooks/usePrev";
import { isEqual } from "lodash";

const CARDS_LIMIT = 1;

const exTypeList = ["inwork", "indebt", "zero", "debt50", "hidden", "nodebt"];
const adTypeList = ["inwork", "all"];

const CardsListContainer = ({
  cards,
  search,
  setSearch,
  setLoading,
  setCards,
  permissions,
  ...props
}) => {
  const globalsContext = useGlobals();
  const notificationContext = useNotifications();
  const eventEmitter = useEventEmitter();

  const canTakeOn = cards.user_cards_cnt < CARDS_LIMIT;

  const currentSearch = useThis(search);

  const allTypesAllowed = perm.allowAllCards(permissions);
  const exAllowed = perm.allowExCards(permissions);
  const mediaAllowed = perm.allowMediaCards(permissions);

  const prevSearch = usePrev(search);

  const getType = s => {
    if (s.term) {
      return "";
    }

    if (allTypesAllowed) {
      return s.type || "ex";
    } else if (exAllowed) {
      return "ex";
    } else if (mediaAllowed) {
      return "ad";
    }
  };

  const getStatus = s => {
    if (s.term) {
      return "";
    }

    const type = getType(s);

    if (type == "ex") {
      if (!exTypeList.includes(s.status)) {
        return "indebt";
      }

      return s.status;
    }

    if (type == "ad") {
      if (!adTypeList.includes(s.status)) {
        return "all";
      }

      return s.status;
    }
  };

  const type = getType(currentSearch.current);
  const status = getStatus(currentSearch.current);

  const load = s => {
    // TODO: fix this in useDebounce:
    // it should not emit change on first run and without actual change
    if (isEqual(s, prevSearch)) {
      return;
    }

    setLoading(true);

    const type = getType(s);
    const status = getStatus(s);
    const term = s.term || "";

    const searchParams = { type, status, term };

    CardsService(searchParams)
      .then(cards => setCards(cards))
      .catch(e => {
        setCards({ counts: {}, cards: [] });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onTakeOn = ({ id, userId }) => {
    CardTakeOnService({ id, userId })
      .then(() => {})
      .catch(e => {
        if (e.code == "CARD_ALREADY_TAKEN") {
          notificationContext.showSimpleNotification({
            title: "Задание уже занято ",
            text:
              "Данная карточка уже взята другим редактором. Пожалуйста, возьмите следующую.",
            color: "red"
          });
        } else if (e.code == "CARD_LIMIT_SUCCEED") {
          notificationContext.showSimpleNotification({
            title: "Ошибка",
            text: "Достигнут лимит на количество карточек в работе",
            color: "red"
          });
        }
      });
  };

  const onTakeOff = ({ id, userId }) => {
    CardTakeOffService({ id, userId })
      .then(() => {})
      .catch(e => {});
  };

  const onSetHidden = ({ id, hidden }) => {
    CardSetHiddenService({ cid: id, hidden })
      .then(() => {})
      .catch(e => {
        console.log(e);
      });
  };

  const onSetRatio = ({ id, ratio }) => {
    CardSetRatioService({ id, ratio }).catch(e => {
      console.log(e);
    });
  };

  const onSetPacket = ({ id, packet }) => {
    CardSetPacketService({ id, packet }).catch(e => {
      console.log(e);
    });
  };

  useEffect(() => {
    load(search);
  }, [search]);

  const handleUpdate = () => {
    load(currentSearch.current);
  };

  useEffect(() => {
    eventEmitter.subscribe("cards_update", handleUpdate);

    return () => {
      eventEmitter.unsubscribe("cards_update", handleUpdate);
    };
  }, []);

  const onTypeChange = type => {
    const newSearch = { type, term: "" };

    if (type == "ex") {
      if (!exTypeList.includes(type)) {
        newSearch.status = "indebt";
      } else {
        newSearch.status = search.status;
      }
    } else if (type == "ad") {
      if (!adTypeList.includes(type)) {
        newSearch.status = "all";
      } else {
        newSearch.status = search.status;
      }
    }

    setSearch(newSearch);
  };

  const onStatusChange = status => {
    setSearch({ ...currentSearch.current, status, term: "" });
  };

  const onSearchChange = term => {
    setSearch({ ...currentSearch.current, term });
  };

  return (
    <CardsList
      {...props}
      cards={cards}
      loading={cards.loading}
      type={type}
      status={status}
      onTypeChange={onTypeChange}
      onStatusChange={onStatusChange}
      term={search.term}
      onTakeOn={onTakeOn}
      onTakeOff={onTakeOff}
      onSetHidden={onSetHidden}
      onSetRatio={onSetRatio}
      onSetPacket={onSetPacket}
      canTakeOn={canTakeOn}
      user={globalsContext.user}
      onSearchChange={onSearchChange}
      allTypesAllowed={allTypesAllowed}
      permissions={permissions}
    />
  );
};

export default CardsListContainer;
